import { useCallback, useEffect, useMemo, useState, useContext, createContext } from "react"
import { gql } from "@apollo/client"
import { useConfigContext } from "@app/providers/config"
import { useCustomerContext } from "@app/providers/customer"
import { useCore, useStorage } from "@app/hooks/useCore"
import { useShopify } from "@app/hooks/useShopify"

const SWISH_SDK_URL = "https://swish.app/cdn/sdk@0.151.0/swish.js"
const DEFAULT_WISHLIST_NAME = "Your Favourites"
const DEFAULT_WISHLIST_ID = "default"
const PAGE_LIMIT = 100
const NODES_LIMIT = 250

const GET_LOCALIZATION = gql`
  query SwishLocalization {
    localization {
      country {
        isoCode
      }
      language {
        isoCode
      }
      market {
        handle
      }
    }
  }
`

const GET_PRODUCT_HANDLES = gql`
  query SwishProductHandles($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
      }
    }
  }
`

type WishList = {
  id: string
  title: string
  access: "private" | "public"
  itemIds: Array<string>
  products: Array<{ handle: string }>
}

type ProductLike = { id?: string } | undefined

type ContextProps = {
  fetched: boolean
  wishLists: Array<WishList>
  selectableWishLists: Array<WishList>
  unlistedProducts: Array<{ handle: string }>
  hasProfile: boolean
  sharedWishList: WishList | null
  isLoadingDelete: boolean
  getWishListsContaining: (product: ProductLike) => Array<WishList>
  saveProductToWishLists: (args: { product: ProductLike; variant?: ProductLike; wishListIds: Array<string> }) => Promise<void>
  tryCreateWishList: (name?: string) => Promise<void>
  renameWishList: (id: string, name: string) => Promise<void>
  deleteWishList: (id: string) => Promise<void>
  shareWishList: (id: string) => Promise<void>
  fetchSharedWishList: (id: string) => Promise<void>
}

type SwishResult = { data?: any; pageInfo?: { next: string | null }; error?: { message?: string } }

type SwishApp = {
  api: {
    hasProfile: () => boolean
    lists: {
      list: (query?: { limit?: number; page?: string }) => Promise<SwishResult>
      findById: (listId: string, options?: { shared?: boolean }) => Promise<SwishResult>
      create: (list: { name: string; items?: Array<{ productId: number; variantId?: number }> }) => Promise<SwishResult>
      updateById: (listId: string, body: { name?: string; access?: "private" | "public" }) => Promise<SwishResult>
      deleteById: (listId: string) => Promise<SwishResult>
    }
    items: {
      list: (query?: { limit?: number; page?: string; listId?: string }, options?: { shared?: boolean }) => Promise<SwishResult>
      create: (item: { productId: number; variantId?: number }) => Promise<SwishResult>
      deleteById: (itemId: string) => Promise<SwishResult>
      setListsById: (itemId: string, listIds: Array<string>) => Promise<SwishResult>
    }
  }
}

const unwrap = (result: SwishResult) => {
  if (result.error) throw new Error(result.error.message || "Swish request failed")
  return result.data
}

const fetchAllPages = async (fetchPage: (page?: string) => Promise<SwishResult>): Promise<Array<any>> => {
  const results: Array<any> = []
  let page: string | undefined
  do {
    const response = await fetchPage(page)
    results.push(...(unwrap(response) || []))
    page = response.pageInfo?.next || undefined
  } while (page)
  return results
}

const setCustomerTokenCookie = (name: string, path: string, token?: { accessToken?: string; expiresAt?: string }) => {
  const maxAge = token?.expiresAt ? Math.max(0, Math.floor((new Date(token.expiresAt).getTime() - Date.now()) / 1000)) : 0
  const value = token?.accessToken && maxAge ? encodeURIComponent(token.accessToken) : ""
  document.cookie = `${name}=${value}; Path=${path}; Max-Age=${value ? maxAge : 0}; SameSite=Strict; Secure`
}

export const WishListContext = createContext<ContextProps | undefined>(undefined)

export const WishListProvider: React.FC = ({ children }) => {
  const {
    store,
    settings: { keys, functions, routes },
  } = useConfigContext()
  const { customer, customerLoading } = useCustomerContext()
  const { getStorage } = useStorage()
  const { client } = useShopify()
  const {
    helpers: { isBrowser, decodeShopifyId },
  } = useCore()

  const [swish, setSwish] = useState<SwishApp | null>(null)
  const [sharedRequested, setSharedRequested] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [fetchFailed, setFetchFailed] = useState(false)
  const [hasProfile, setHasProfile] = useState(false)
  const [unlistedProducts, setUnlistedProducts] = useState<Array<{ handle: string }>>([])
  const [wishLists, setWishLists] = useState<Array<WishList>>([])
  const [itemIdByProductId, setItemIdByProductId] = useState<Record<number, string>>({})
  const [sharedWishList, setSharedWishList] = useState<WishList | null>(null)
  const [pendingSharedId, setPendingSharedId] = useState<string | null>(null)
  const [isLoadingDelete, setIsLoadingDelete] = useState(false)

  const customerId = customer ? decodeShopifyId(customer.id, "Customer") : null
  const enabled = isBrowser && !customerLoading && (!!customerId || sharedRequested)

  useEffect(() => {
    setSwish(null)
    setFetched(false)
    setHasProfile(false)
    setUnlistedProducts([])
    setWishLists([])
    setItemIdByProductId({})

    if (!enabled) {
      if (isBrowser) setCustomerTokenCookie(keys.swishCustomerToken, `/api/${functions.swishToken}`)
      return
    }

    let cancelled = false

    const initialise = async () => {
      try {
        setCustomerTokenCookie(keys.swishCustomerToken, `/api/${functions.swishToken}`, customerId ? getStorage(keys.customer) : undefined)

        delete (window as Window & { swish?: SwishApp }).swish

        const [{ createSwish }, { data: localizationData }] = await Promise.all([
          import(/* webpackIgnore: true */ SWISH_SDK_URL),
          client.query({ query: GET_LOCALIZATION, fetchPolicy: "no-cache" }),
        ])
        const localization = localizationData?.localization

        const instance: SwishApp = await createSwish({
          auth: { tokenUrl: `/api/${functions.swishToken}` },
          storefront: { storeDomain: store.shopifyShopDomain, accessToken: store.shopifyStorefrontToken },
          context: {
            customer: {
              id: customerId,
              email: customer?.email || null,
              firstName: customer?.firstName || null,
              lastName: customer?.lastName || null,
              b2b: false,
            },
            localization: {
              country: localization?.country?.isoCode || store.locationRegion,
              language: localization?.language?.isoCode || "EN",
              market: localization?.market?.handle || store.locationRegion?.toLowerCase(),
            },
            myshopifyDomain: store.shopifyShopDomain,
            shopName: store.title,
            newCustomerAccounts: false,
            routes: { accountUrl: routes.DASHBOARD },
          },
        })

        if (!cancelled) setSwish(instance)
      } catch (e) {
        console.error((e as Error).message)
      }
    }

    initialise()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, customerId])

  const getHandlesByProductId = useCallback(
    async (productIds: Array<number>) => {
      const uniqueIds = [...new Set(productIds)]
      const chunks = Array.from({ length: Math.ceil(uniqueIds.length / NODES_LIMIT) }, (_, index) =>
        uniqueIds.slice(index * NODES_LIMIT, (index + 1) * NODES_LIMIT)
      )

      const responses = await Promise.all(
        chunks.map(ids =>
          client.query({
            query: GET_PRODUCT_HANDLES,
            variables: { ids: ids.map(id => `gid://shopify/Product/${id}`) },
            fetchPolicy: "no-cache",
          })
        )
      )

      return responses
        .flatMap(({ data }) => data?.nodes || [])
        .reduce((handles: Record<number, string>, node: any) => {
          const id = Number(node?.id?.split("/").pop())
          if (id && node?.handle) handles[id] = node.handle
          return handles
        }, {})
    },
    [client]
  )

  const normaliseList = useCallback(
    (list: any, productIdByItemId: Record<string, number>, handles: Record<number, string>): WishList => ({
      id: list.id,
      title: list.name,
      access: list.access,
      itemIds: list.itemIds || [],
      products: (list.itemIds || [])
        .map((itemId: string) => handles[productIdByItemId[itemId]])
        .filter(Boolean)
        .map((handle: string) => ({ handle })),
    }),
    []
  )

  const refreshWishLists = useCallback(async () => {
    if (!swish || !customerId) return

    try {
      const [lists, items] = await Promise.all([
        fetchAllPages(page => swish.api.lists.list({ limit: PAGE_LIMIT, page })),
        fetchAllPages(page => swish.api.items.list({ limit: PAGE_LIMIT, page })),
      ])

      const productIdByItemId = items.reduce((map: Record<string, number>, item: any) => ({ ...map, [item.id]: item.productId }), {})
      const handles = await getHandlesByProductId(items.map((item: any) => item.productId))

      setItemIdByProductId(items.reduce((map: Record<number, string>, item: any) => ({ ...map, [item.productId]: item.id }), {}))
      const userLists = lists.filter((list: any) => !list.type || list.type === "user")
      const listedItemIds = new Set(userLists.flatMap((list: any) => list.itemIds || []))

      setWishLists(userLists.map(list => normaliseList(list, productIdByItemId, handles)))
      setUnlistedProducts(
        items
          .filter((item: any) => !listedItemIds.has(item.id))
          .map((item: any) => handles[item.productId])
          .filter(Boolean)
          .map((handle: string) => ({ handle }))
      )
      setFetchFailed(false)
      setHasProfile(swish.api.hasProfile())
    } catch (e) {
      console.error((e as Error).message)
      setFetchFailed(true)
    } finally {
      setFetched(true)
    }
  }, [swish, customerId, getHandlesByProductId, normaliseList])

  useEffect(() => {
    refreshWishLists()
  }, [refreshWishLists])

  const loadSharedWishList = useCallback(
    async (id: string) => {
      if (!swish) return

      try {
        const list = unwrap(await swish.api.lists.findById(id, { shared: true }))
        const items = await fetchAllPages(page => swish.api.items.list({ listId: id, limit: PAGE_LIMIT, page }, { shared: true }))
        const productIdByItemId = items.reduce((map: Record<string, number>, item: any) => ({ ...map, [item.id]: item.productId }), {})
        const handles = await getHandlesByProductId(items.map((item: any) => item.productId))

        setSharedWishList(normaliseList({ ...list, itemIds: items.map((item: any) => item.id) }, productIdByItemId, handles))
      } catch (e) {
        console.error((e as Error).message)
        setSharedWishList(null)
      }
    },
    [swish, getHandlesByProductId, normaliseList]
  )

  useEffect(() => {
    if (swish && pendingSharedId) loadSharedWishList(pendingSharedId)
  }, [swish, pendingSharedId, loadSharedWishList])

  const fetchSharedWishList = useCallback(async (id: string) => {
    setSharedRequested(true)
    setPendingSharedId(id)
  }, [])

  const getWishListsContaining = useCallback(
    (product: ProductLike) => {
      const productId = Number(decodeShopifyId(product?.id, "Product"))
      const itemId = itemIdByProductId[productId]
      if (!itemId) return []

      return wishLists.filter(list => list.itemIds.includes(itemId))
    },
    [decodeShopifyId, itemIdByProductId, wishLists]
  )

  const tryCreateWishList = useCallback(
    async (name?: string) => {
      if (!swish) return

      try {
        unwrap(await swish.api.lists.create({ name: name || `WishList-${wishLists.length + 1}` }))
        await refreshWishLists()
      } catch (e) {
        console.error((e as Error).message)
      }
    },
    [swish, wishLists.length, refreshWishLists]
  )

  const selectableWishLists = useMemo(
    () =>
      wishLists.length || !fetched || fetchFailed
        ? wishLists
        : [{ id: DEFAULT_WISHLIST_ID, title: DEFAULT_WISHLIST_NAME, access: "private" as const, itemIds: [], products: [] }],
    [wishLists, fetched, fetchFailed]
  )

  const saveProductToWishLists = useCallback(
    async ({ product, variant, wishListIds }: { product: ProductLike; variant?: ProductLike; wishListIds: Array<string> }) => {
      if (!swish) return

      const productId = Number(decodeShopifyId(product?.id, "Product"))
      const variantId = variant?.id ? Number(decodeShopifyId(variant.id, "ProductVariant")) : undefined
      if (!productId) return

      try {
        const existingItemId = itemIdByProductId[productId]
        const item = { productId, ...(variantId && { variantId }) }

        if (wishListIds.includes(DEFAULT_WISHLIST_ID)) {
          if (existingItemId) {
            const list = unwrap(await swish.api.lists.create({ name: DEFAULT_WISHLIST_NAME }))
            if (list?.id) unwrap(await swish.api.items.setListsById(existingItemId, [list.id]))
          } else {
            unwrap(await swish.api.lists.create({ name: DEFAULT_WISHLIST_NAME, items: [item] }))
          }
        } else if (!wishListIds.length) {
          if (existingItemId) unwrap(await swish.api.items.deleteById(existingItemId))
        } else {
          const itemId = existingItemId || unwrap(await swish.api.items.create(item))?.id
          if (itemId) unwrap(await swish.api.items.setListsById(itemId, wishListIds))
        }

        await refreshWishLists()
      } catch (e) {
        console.error((e as Error).message)
      }
    },
    [swish, decodeShopifyId, itemIdByProductId, refreshWishLists]
  )

  const renameWishList = useCallback(
    async (id: string, name: string) => {
      if (!swish) return

      try {
        unwrap(await swish.api.lists.updateById(id, { name }))
        await refreshWishLists()
      } catch (e) {
        console.error((e as Error).message)
      }
    },
    [swish, refreshWishLists]
  )

  const deleteWishList = useCallback(
    async (id: string) => {
      if (!swish) return

      setIsLoadingDelete(true)
      try {
        unwrap(await swish.api.lists.deleteById(id))
        await refreshWishLists()
      } catch (e) {
        console.error((e as Error).message)
      } finally {
        setIsLoadingDelete(false)
      }
    },
    [swish, refreshWishLists]
  )

  const shareWishList = useCallback(
    async (id: string) => {
      if (!swish) return

      try {
        unwrap(await swish.api.lists.updateById(id, { access: "public" }))
      } catch (e) {
        console.error((e as Error).message)
      }
    },
    [swish]
  )

  const contextValue = useMemo<ContextProps>(
    () => ({
      fetched,
      wishLists,
      sharedWishList,
      isLoadingDelete,
      getWishListsContaining,
      saveProductToWishLists,
      selectableWishLists,
      unlistedProducts,
      hasProfile,
      tryCreateWishList,
      renameWishList,
      deleteWishList,
      shareWishList,
      fetchSharedWishList,
    }),
    [
      fetched,
      wishLists,
      sharedWishList,
      isLoadingDelete,
      getWishListsContaining,
      saveProductToWishLists,
      selectableWishLists,
      unlistedProducts,
      hasProfile,
      tryCreateWishList,
      renameWishList,
      deleteWishList,
      shareWishList,
      fetchSharedWishList,
    ]
  )

  return <WishListContext.Provider value={contextValue}>{children}</WishListContext.Provider>
}

export const useWishListContext = (): ContextProps => ({ ...useContext(WishListContext) } as ContextProps)
export type { WishList }
